import React, { useState } from "react"
import {
  FaBuilding, FaPlus, FaSearch, FaFileExcel, FaSort, FaSortUp, FaSortDown, FaMoneyBillWave,
} from "react-icons/fa"
import {
  FiFilter, FiX, FiCalendar, FiRefreshCw, FiEdit2,
} from "react-icons/fi"
import { LuEye } from "react-icons/lu"
import { useNavigate } from "react-router-dom"
import { PaginationType } from "../../../utils/pagination.utils"
import { ExcelDownload } from "../../../utils/excel.utils"
import { SelectWithSearch2, type SelectOption } from "../../../utils/select-with-search"
import axiosInstance from "../../../libs/axios"
import toast from "react-hot-toast"

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface SupplierPaymentRequest {
  id: number
  employee_id: number
  supplier_name: string
  reason_for_request: string
  other_remarks?: string | null
  request_description?: string | null
  total_payable_amount: number
  bank_name?: string | null
  ifsc_code?: string | null
  account_number?: string | null
  upi_id?: string | null
  qr_code?: string | null
  status: string
  approved_by?: number | null
  approved_at?: string | null
  approved_amount?: number | null
  approver_remarks?: string | null
  rejected_by?: number | null
  rejected_at?: string | null
  rejection_remarks?: string | null
  transferred_by?: number | null
  transferred_at?: string | null
  transferred_amount?: number | null
  transfer_remarks?: string | null
  transfer_date?: string | null
  completed_by?: number | null
  completed_at?: string | null
  completion_remarks?: string | null
  completion_date?: string | null
  cancelled_by?: number | null
  cancelled_at?: string | null
  cancellation_remarks?: string | null
  created_at: string
  updated_at: string
  employee?: {
    id: number
    first_name: string
    last_name: string
    employee_code?: string
    association_type?: string
    designation?: { id: number; name: string } | null
    department?: { id: number; name: string } | null
  }
  approver?: { id: number; first_name: string; last_name: string } | null
  rejector?: { id: number; first_name: string; last_name: string } | null
  transferrer?: { id: number; first_name: string; last_name: string } | null
  completer?: { id: number; first_name: string; last_name: string } | null
}

export interface SPFilters {
  employee_id?: string
  employee_name?: string
  reason_for_request?: string
  amount_min?: string
  amount_max?: string
  created_at_from?: string
  created_at_to?: string
  approved_at_from?: string
  approved_at_to?: string
  transfer_date_from?: string
  transfer_date_to?: string
  approved_by?: string
  approved_by_name?: string
}

interface SupplierPaymentListProps {
  requests: SupplierPaymentRequest[]
  pagination: PaginationType
  loading: boolean
  searchTerm: string
  statusFilter: string
  onSearch: (value: string) => void
  onStatusChange: (value: string) => void
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  onCreateRequest: () => void
  canCreate: boolean
  canViewAll: boolean
  canTransfer: boolean
  canEdit: boolean
  onEditRequest: (req: SupplierPaymentRequest) => void
  sortBy: string
  sortOrder: string
  onSort: (field: string) => void
  filters: SPFilters
  onFiltersChange: (filters: SPFilters) => void
  onApplyFilters: () => void
  onResetFilters: () => void
  onRefresh: () => void
}

// ─── Lookups ────────────────────────────────────────────────────────────────────

const statusColors: Record<string, string> = {
  requested: "bg-blue-100 text-blue-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
  payment: "bg-amber-100 text-amber-800",
  completed: "bg-green-200 text-green-900",
  cancelled: "bg-stone-200 text-stone-800",
}

const statusLabels: Record<string, string> = {
  requested: "Requested",
  approved: "Approved",
  rejected: "Rejected",
  payment: "Payment",
  completed: "Completed",
  cancelled: "Cancelled",
}

const reasonLabels: Record<string, string> = {
  purchase: "Purchase",
  repair: "Repair",
  office_expenses: "Office Expenses",
  other: "Other",
}

// ─── Helpers ────────────────────────────────────────────────────────────────────

function fmtAmt(amount?: number | null) {
  if (amount === undefined || amount === null) return "—"
  return `₹${Number(amount).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`
}

function fmtDate(dateStr?: string | null) {
  if (!dateStr) return "—"
  return new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
}

function fmtDateTime(dateStr?: string | null) {
  if (!dateStr) return "—"
  return new Date(dateStr).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  })
}

function fullName(e?: { first_name: string; last_name: string } | null) {
  return e ? `${e.first_name} ${e.last_name}` : "—"
}

// ─── Sort Header ────────────────────────────────────────────────────────────────

const SortHeader: React.FC<{
  label: string; field: string; sortBy: string; sortOrder: string; onSort: (f: string) => void; className?: string
}> = ({ label, field, sortBy, sortOrder, onSort, className }) => {
  const active = sortBy === field
  return (
    <th
      className={`px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100 ${className || ""}`}
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {active ? (
          sortOrder === "ASC" ? <FaSortUp className="text-blue-600 w-3 h-3" /> : <FaSortDown className="text-blue-600 w-3 h-3" />
        ) : (
          <FaSort className="text-gray-400 w-3 h-3" />
        )}
      </div>
    </th>
  )
}

// ─── Filter Modal ───────────────────────────────────────────────────────────────

interface FilterModalProps {
  isOpen: boolean
  onClose: () => void
  filters: SPFilters
  onFiltersChange: (f: SPFilters) => void
  onApply: () => void
  onReset: () => void
}

const SPFilterModal: React.FC<FilterModalProps> = ({ isOpen, onClose, filters, onFiltersChange, onApply, onReset }) => {
  const [activeTab, setActiveTab] = useState("basic")
  const [selectedEmployee, setSelectedEmployee] = useState<SelectOption | null>(
    filters.employee_id ? { id: Number(filters.employee_id), value: Number(filters.employee_id), label: filters.employee_name || `ID: ${filters.employee_id}` } : null
  )
  const [selectedApprover, setSelectedApprover] = useState<SelectOption | null>(
    filters.approved_by ? { id: Number(filters.approved_by), value: Number(filters.approved_by), label: filters.approved_by_name || `ID: ${filters.approved_by}` } : null
  )

  if (!isOpen) return null

  const set = (field: keyof SPFilters, value: string) =>
    onFiltersChange({ ...filters, [field]: value || undefined })

  const tabs = [
    { id: "basic", label: "Basic" },
    { id: "amount", label: "Amounts" },
    { id: "dates", label: "Dates" },
    { id: "approval", label: "Approval" },
  ]

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-2xl max-w-3xl w-full max-h-[88vh] min-h-[440px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <FiFilter className="w-5 h-5" /> Advanced Filters
          </h2>
          <button onClick={onClose} className="text-white hover:text-gray-200"><FiX className="w-6 h-6" /></button>
        </div>

        {/* Tabs */}
        <div className="flex bg-gray-50 border-b border-gray-200 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id ? "border-blue-500 text-blue-600 bg-white" : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {activeTab === "basic" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Employee</label>
                <SelectWithSearch2
                  apiEndpoint="/users/list"
                  value={selectedEmployee}
                  onChange={(opt) => {
                    setSelectedEmployee(opt)
                    onFiltersChange({ ...filters, employee_id: opt ? String(opt.value) : undefined, employee_name: opt ? opt.label : undefined })
                  }}
                  placeholder="Search employee..."
                  labelKey="first_name"
                  valueKey="id"
                  dataKey="users"
                  displayFormat={(u: any) => `${u.first_name} ${u.last_name}${u.employee_code ? ` (${u.employee_code})` : ""}`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Reason</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={filters.reason_for_request || ""}
                  onChange={(e) => set("reason_for_request", e.target.value)}
                >
                  <option value="">All Reasons</option>
                  {Object.entries(reasonLabels).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {activeTab === "amount" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Min Amount (₹)</label>
                <input
                  type="number" min={0}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. 1000"
                  value={filters.amount_min || ""}
                  onChange={(e) => set("amount_min", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Amount (₹)</label>
                <input
                  type="number" min={0}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. 50000"
                  value={filters.amount_max || ""}
                  onChange={(e) => set("amount_max", e.target.value)}
                />
              </div>
            </div>
          )}

          {activeTab === "dates" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Request Date From</label>
                <input type="date" className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={filters.created_at_from || ""} onChange={(e) => set("created_at_from", e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Request Date To</label>
                <input type="date" className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={filters.created_at_to || ""} onChange={(e) => set("created_at_to", e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Transfer Date From</label>
                <input type="date" className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={filters.transfer_date_from || ""} onChange={(e) => set("transfer_date_from", e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Transfer Date To</label>
                <input type="date" className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={filters.transfer_date_to || ""} onChange={(e) => set("transfer_date_to", e.target.value)} />
              </div>
            </div>
          )}

          {activeTab === "approval" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Approved By</label>
                <SelectWithSearch2
                  apiEndpoint="/users/list"
                  value={selectedApprover}
                  onChange={(opt) => {
                    setSelectedApprover(opt)
                    onFiltersChange({ ...filters, approved_by: opt ? String(opt.value) : undefined, approved_by_name: opt ? opt.label : undefined })
                  }}
                  placeholder="Search approver..."
                  labelKey="first_name"
                  valueKey="id"
                  dataKey="users"
                  displayFormat={(u: any) => `${u.first_name} ${u.last_name}`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Approved From</label>
                <input type="date" className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={filters.approved_at_from || ""} onChange={(e) => set("approved_at_from", e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Approved To</label>
                <input type="date" className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={filters.approved_at_to || ""} onChange={(e) => set("approved_at_to", e.target.value)} />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <button onClick={onReset} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-100">
            Reset Filters
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-100">
              Cancel
            </button>
            <button onClick={() => { onApply(); onClose(); }} className="px-4 py-2 text-sm bg-blue-800 text-white rounded hover:bg-blue-900">
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Transfer Drawer ────────────────────────────────────────────────────────────

interface TransferDrawerProps {
  open: boolean
  request: SupplierPaymentRequest | null
  onClose: () => void
  onSuccess: () => void
}

const TransferDrawer: React.FC<TransferDrawerProps> = ({ open, request, onClose, onSuccess }) => {
  const [transferAmount, setTransferAmount] = useState("")
  const [transferRemarks, setTransferRemarks] = useState("")
  const [transferDate, setTransferDate] = useState("")
  const [loading, setLoading] = useState(false)

  React.useEffect(() => {
    if (open && request) {
      setTransferAmount(String(request.approved_amount ?? request.total_payable_amount))
      setTransferDate("")
      setTransferRemarks("")
    }
  }, [open, request])

  if (!open || !request) return null

  const handleTransfer = async () => {
    if (!transferDate) { toast.error("Transfer date is required"); return }
    try {
      setLoading(true)
      await axiosInstance.put("/supplier-payment/transfer", {
        id: request.id,
        transferred_amount: transferAmount ? Number(transferAmount) : undefined,
        transfer_remarks: transferRemarks.trim() || undefined,
        transfer_date: transferDate,
      })
      toast.success("Payment transferred successfully")
      onClose()
      onSuccess()
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to transfer payment")
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={loading ? undefined : onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-lg bg-white shadow-xl z-50 flex flex-col">
        <div className="flex items-center justify-between p-5 border-b bg-gray-50">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Transfer Payment</h3>
            <p className="text-sm text-gray-500 mt-0.5">SP #{request.id} · {request.supplier_name}</p>
          </div>
          <button onClick={onClose} disabled={loading} className="p-2 rounded hover:bg-gray-200 disabled:opacity-50">
            <FiX size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Transferred Amount</label>
            <input type="number" value={transferAmount} onChange={(e) => setTransferAmount(e.target.value)}
              placeholder="Enter amount transferred"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-yellow-500" />
            <p className="text-xs text-gray-400 mt-1">Approved: {fmtAmt(request.approved_amount)}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Transfer Date *</label>
            <input type="date" value={transferDate} onChange={(e) => setTransferDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-yellow-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Transfer Remarks (optional)</label>
            <textarea value={transferRemarks} onChange={(e) => setTransferRemarks(e.target.value)}
              rows={2} placeholder="Add transfer remarks..."
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-yellow-500" />
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-xs text-yellow-800">
            This will mark the payment as transferred. The employee will be notified.
          </div>
        </div>

        <div className="border-t bg-gray-50 px-5 py-4 flex justify-end gap-3">
          <button onClick={onClose} disabled={loading} className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={handleTransfer} disabled={loading || !transferDate}
            className="px-4 py-2 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50">
            {loading ? "Transferring..." : "Confirm Transfer"}
          </button>
        </div>
      </div>
    </>
  )
}

// ─── Main List Component ────────────────────────────────────────────────────────

export const SupplierPaymentList: React.FC<SupplierPaymentListProps> = ({
  requests,
  pagination,
  loading,
  searchTerm,
  statusFilter,
  onSearch,
  onStatusChange,
  onPageChange,
  onLimitChange,
  onCreateRequest,
  canCreate,
  canViewAll,
  canTransfer,
  canEdit,
  onEditRequest,
  sortBy,
  sortOrder,
  onSort,
  filters,
  onFiltersChange,
  onApplyFilters,
  onResetFilters,
  onRefresh,
}) => {
  const navigate = useNavigate()

  // Filter modal
  const [showFilters, setShowFilters] = useState(false)
  // Transfer drawer
  const [transferRequest, setTransferRequest] = useState<SupplierPaymentRequest | null>(null)
  // Excel export
  const [showExport, setShowExport] = useState(false)

  const activeFilterCount = Object.values(filters).filter(v => v !== undefined && v !== "").length

  // Prepare excel data from current page
  const excelData = requests.map(req => ({
    "SP ID": req.id,
    "Employee": fullName(req.employee),
    "Employee Code": req.employee?.employee_code || "",
    "Designation": req.employee?.designation?.name || "",
    "Department": req.employee?.department?.name || "",
    "Association Type": req.employee?.association_type || "",
    "Supplier Name": req.supplier_name,
    "Reason": reasonLabels[req.reason_for_request] || req.reason_for_request,
    "Other Remarks": req.other_remarks || "",
    "Description": req.request_description || "",
    "Requested Amount": req.total_payable_amount,
    "Approved Amount": req.approved_amount ?? "",
    "Transferred Amount": req.transferred_amount ?? "",
    "Status": statusLabels[req.status] || req.status,
    "Approver": fullName(req.approver),
    "Approver Remarks": req.approver_remarks || "",
    "Transferrer": fullName(req.transferrer),
    "Request Date": fmtDateTime(req.created_at),
    "Approve/Reject Date": fmtDateTime(req.approved_at || req.rejected_at),
    "Transfer Date": fmtDate(req.transfer_date),
    "Completion Date": fmtDate(req.completed_at || req.completion_date),
    "Updated At": fmtDateTime(req.updated_at),
  }))

  return (
    <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">

      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Supplier Payment Requests</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {canViewAll ? "All requests" : "My requests"} · {pagination.total_count} total
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setShowExport(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm transition-colors"
              title="Export to Excel"
            >
              <FaFileExcel className="w-4 h-4" /> Export
            </button>
            <button
              onClick={onRefresh}
              className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm transition-colors"
              title="Refresh"
            >
              <FiRefreshCw className="w-4 h-4" />
            </button>
            {canCreate && (
              <button
                onClick={onCreateRequest}
                className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center gap-2 transition-colors text-sm font-medium shadow-sm"
              >
                <FaPlus className="w-4 h-4" /> New Request
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Search, Status & Filters */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
        <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
          <div className="flex-1 min-w-[180px] max-w-md">
            <label className="block text-xs font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
              <input
                type="text"
                placeholder="Search supplier name, description…"
                value={searchTerm}
                onChange={(e) => onSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </div>
          <div className="w-full sm:w-44">
            <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => onStatusChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm bg-white"
            >
              <option value="">All Statuses</option>
              {Object.entries(statusLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setShowFilters(true)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded text-sm border transition-colors ${
                activeFilterCount > 0
                  ? "bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FiFilter className="w-4 h-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-xs bg-blue-600 text-white rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[110px]">Action</th>
              <SortHeader label="User Name" field="employee_id" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} className="min-w-[160px]" />
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[130px]">Employee Code</th>
              <SortHeader label="Designation" field="designation" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} className="min-w-[140px]" />
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[140px]">Department</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[140px]">Reporting Person</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[130px]">Association Type</th>
              <SortHeader label="SP Stage" field="status" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} className="min-w-[130px]" />
              <SortHeader label="Supplier Name" field="supplier_name" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} className="min-w-[160px]" />
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[130px]">Reason</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[160px]">Other Remarks</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[180px]">Request Description</th>
              <SortHeader label="Requested Amount" field="total_payable_amount" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} className="min-w-[150px]" />
              <SortHeader label="Approved Amount" field="approved_amount" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} className="min-w-[140px]" />
              <SortHeader label="Transferred Amount" field="transferred_amount" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} className="min-w-[150px]" />
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[140px]">Approver Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[140px]">Transferrer Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[160px]">Approver Remarks</th>
              <SortHeader label="Request Date & Time" field="created_at" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} className="min-w-[170px]" />
              <SortHeader label="Approve/Reject Date" field="approved_at" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} className="min-w-[180px]" />
              <SortHeader label="Transfer Date" field="transfer_date" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} className="min-w-[130px]" />
              <SortHeader label="Completion Date" field="completed_at" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} className="min-w-[140px]" />
              <SortHeader label="Updated Date & Time" field="updated_at" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} className="min-w-[170px]" />
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={23} className="px-4 sm:px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                    <span className="text-sm text-gray-600">Loading requests...</span>
                  </div>
                </td>
              </tr>
            ) : requests.length === 0 ? (
              <tr>
                <td colSpan={23} className="px-4 sm:px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <FaBuilding className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No requests found</p>
                    <p className="text-xs text-gray-400 mt-1">Try adjusting your search or filters</p>
                  </div>
                </td>
              </tr>
            ) : (
              requests.map((req) => {
                const approveRejectDate = req.approved_at || req.rejected_at
                return (
                  <tr key={req.id} className="hover:bg-gray-50 transition-colors">

                    {/* Action */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => navigate(`/claims/supplier-payment/${req.id}`)}
                          className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors"
                          title="View details"
                        >
                          <LuEye className="w-4 h-4" />
                        </button>
                        {canEdit && req.status === "requested" && (
                          <button
                            onClick={() => onEditRequest(req)}
                            className="text-emerald-600 hover:text-emerald-800 p-2 hover:bg-emerald-50 rounded transition-colors"
                            title="Edit request"
                          >
                            <FiEdit2 className="w-4 h-4" />
                          </button>
                        )}
                        {canTransfer && req.status === "approved" && (
                          <button
                            onClick={() => setTransferRequest(req)}
                            className="text-yellow-600 hover:text-yellow-800 p-2 hover:bg-yellow-50 rounded transition-colors"
                            title="Transfer payment"
                          >
                            <FaMoneyBillWave className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>

                    {/* User Name */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm font-semibold text-gray-900">{fullName(req.employee)}</span>
                    </td>

                    {/* Employee Code */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 font-mono">{req.employee?.employee_code || "—"}</span>
                    </td>

                    {/* Designation */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{req.employee?.designation?.name || "—"}</span>
                    </td>

                    {/* Department */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{req.employee?.department?.name || "—"}</span>
                    </td>

                    {/* Reporting Person */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-400">—</span>
                    </td>

                    {/* Association Type */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 capitalize">{req.employee?.association_type || "—"}</span>
                    </td>

                    {/* SP Stage */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusColors[req.status] || "bg-gray-100 text-gray-800"}`}>
                        {statusLabels[req.status] || req.status}
                      </span>
                    </td>

                    {/* Supplier Name */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{req.supplier_name}</span>
                    </td>

                    {/* Reason */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{reasonLabels[req.reason_for_request] || req.reason_for_request}</span>
                    </td>

                    {/* Other Remarks */}
                    <td className="px-4 py-4">
                      <span className="text-sm text-gray-900 block max-w-[150px] truncate" title={req.other_remarks || ""}>
                        {req.other_remarks || "—"}
                      </span>
                    </td>

                    {/* Description */}
                    <td className="px-4 py-4">
                      <span className="text-sm text-gray-900 block max-w-[180px] truncate" title={req.request_description || ""}>
                        {req.request_description || "—"}
                      </span>
                    </td>

                    {/* Requested Amount */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm font-semibold text-gray-900">{fmtAmt(req.total_payable_amount)}</span>
                    </td>

                    {/* Approved Amount */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{fmtAmt(req.approved_amount)}</span>
                    </td>

                    {/* Transferred Amount */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{fmtAmt(req.transferred_amount)}</span>
                    </td>

                    {/* Approver Name */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{fullName(req.approver)}</span>
                    </td>

                    {/* Transferrer Name */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{fullName(req.transferrer)}</span>
                    </td>

                    {/* Approver Remarks */}
                    <td className="px-4 py-4">
                      <span className="text-sm text-gray-900 block max-w-[150px] truncate" title={req.approver_remarks || ""}>
                        {req.approver_remarks || "—"}
                      </span>
                    </td>

                    {/* Request Date & Time */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{fmtDateTime(req.created_at)}</span>
                    </td>

                    {/* Approved/Reject Date & Time */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{fmtDateTime(approveRejectDate)}</span>
                    </td>

                    {/* Transfer Date */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{fmtDate(req.transfer_date)}</span>
                    </td>

                    {/* Completion Date */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{fmtDate(req.completed_at || req.completion_date)}</span>
                    </td>

                    {/* Updated Date & Time */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{fmtDateTime(req.updated_at)}</span>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {(pagination.total_pages > 1 || pagination.total_count > 10) && (
        <div className="px-4 sm:px-6 py-4 border-t border-gray-200 bg-gray-50/30 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>Show</span>
            <select
              value={pagination.limit}
              onChange={(e) => onLimitChange(Number(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm bg-white focus:ring-2 focus:ring-blue-500"
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <span>per page · {pagination.total_count} total</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={!pagination.has_prev_page}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-3 py-1.5 text-sm text-gray-700 font-medium">
              {pagination.page} / {pagination.total_pages}
            </span>
            <button
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={!pagination.has_next_page}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      <SPFilterModal
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        onFiltersChange={onFiltersChange}
        onApply={onApplyFilters}
        onReset={onResetFilters}
      />

      <ExcelDownload
        isOpen={showExport}
        onClose={() => setShowExport(false)}
        data={excelData}
        filename="supplier_payments"
        title="Export Supplier Payments"
        description="Export current page data to Excel or CSV"
      />

      <TransferDrawer
        open={transferRequest !== null}
        request={transferRequest}
        onClose={() => setTransferRequest(null)}
        onSuccess={onRefresh}
      />
    </div>
  )
}
